import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Package, Save, X, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VehicleAddonsManager = ({ vehicleId, vehicleName, token, onClose }) => {
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [newAddon, setNewAddon] = useState(null);
  
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAddons = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/vehicles/${vehicleId}/addons`,
        { headers }
      );
      setAddons(response.data.addons || []);
    } catch (error) {
      console.error('Error fetching addons:', error);
      toast.error('Failed to load add-ons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddons();
  }, [vehicleId]);

  const handleCreate = async (addon) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/vehicles/${vehicleId}/addons`,
        null,
        {
          headers,
          params: {
            name_en: addon.name_en,
            name_fr: addon.name_fr,
            price: addon.price,
            is_daily: addon.is_daily
          }
        }
      );
      setAddons([...addons, response.data.addon]);
      setNewAddon(null);
      toast.success('Add-on created');
    } catch (error) {
      console.error('Error creating addon:', error);
      toast.error('Failed to create add-on');
    }
  };

  const handleUpdate = async (addonId, addon) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/vehicles/${vehicleId}/addons/${addonId}`,
        null,
        {
          headers,
          params: {
            name_en: addon.name_en,
            name_fr: addon.name_fr,
            price: addon.price,
            is_daily: addon.is_daily
          }
        }
      );
      setAddons(addons.map(a => a.id === addonId ? { ...a, ...addon } : a));
      setEditingId(null);
      toast.success('Add-on updated');
    } catch (error) {
      console.error('Error updating addon:', error);
      toast.error('Failed to update add-on');
    }
  };

  const handleDelete = async (addonId) => {
    if (!confirm('Are you sure you want to delete this add-on?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/admin/vehicles/${vehicleId}/addons/${addonId}`,
        { headers }
      );
      setAddons(addons.filter(a => a.id !== addonId));
      toast.success('Add-on deleted');
    } catch (error) {
      console.error('Error deleting addon:', error);
      toast.error('Failed to delete add-on');
    }
  };

  const AddonRow = ({ addon, isEditing, isNew }) => {
    const [form, setForm] = useState({
      name_en: addon?.name_en || '',
      name_fr: addon?.name_fr || '',
      price: addon?.price || 0,
      is_daily: addon?.is_daily !== false
    });

    const handleSave = () => {
      if (!form.name_en || !form.name_fr) {
        toast.error('Please fill in both English and French names');
        return;
      }
      if (form.price <= 0) {
        toast.error('Price must be greater than 0');
        return;
      }
      
      if (isNew) {
        handleCreate(form);
      } else {
        handleUpdate(addon.id, form);
      }
    };

    const handleCancel = () => {
      if (isNew) {
        setNewAddon(null);
      } else {
        setEditingId(null);
      }
    };

    if (isEditing || isNew) {
      return (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-sm space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-gray-500 text-xs">Name (EN)</Label>
              <Input
                value={form.name_en}
                onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                className="input-default h-9"
                placeholder="Child Seat"
                data-testid={isNew ? "new-addon-name-en" : "edit-addon-name-en"}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-gray-500 text-xs">Name (FR)</Label>
              <Input
                value={form.name_fr}
                onChange={(e) => setForm({ ...form, name_fr: e.target.value })}
                className="input-default h-9"
                placeholder="Siège enfant"
                data-testid={isNew ? "new-addon-name-fr" : "edit-addon-name-fr"}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-gray-500 text-xs">Price (CHF)</Label>
              <Input
                type="number"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
                className="input-default h-9"
                placeholder="15.00"
                step="0.01"
                min="0"
                data-testid={isNew ? "new-addon-price" : "edit-addon-price"}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-gray-500 text-xs">Pricing Type</Label>
              <Select 
                value={form.is_daily ? "daily" : "onetime"} 
                onValueChange={(v) => setForm({ ...form, is_daily: v === "daily" })}
              >
                <SelectTrigger className="input-default h-9" data-testid={isNew ? "new-addon-type" : "edit-addon-type"}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="daily" className="text-[#181B1F]">Per day (/jour)</SelectItem>
                  <SelectItem value="onetime" className="text-[#181B1F]">One-time (unique)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={handleCancel}
              className="text-gray-500 hover:text-[#181B1F]"
            >
              <X className="w-4 h-4 mr-1" />
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              className="btn-primary"
              data-testid={isNew ? "save-new-addon" : "save-edit-addon"}
            >
              <Check className="w-4 h-4 mr-1" />
              Save
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-sm hover:bg-gray-100 transition-colors">
        <div className="flex items-center gap-3">
          <Package className="w-5 h-5 text-primary" />
          <div>
            <div className="text-[#181B1F] font-medium">{addon.name_en}</div>
            <div className="text-gray-500 text-sm">{addon.name_fr}</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-[#181B1F] font-medium">CHF {addon.price?.toFixed(2)}</div>
            <Badge variant={addon.is_daily ? "default" : "secondary"} className="text-xs">
              {addon.is_daily ? 'Per day' : 'One-time'}
            </Badge>
          </div>
          <div className="flex gap-1">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setEditingId(addon.id)}
              className="text-gray-500 hover:text-[#181B1F] p-2"
              data-testid={`edit-addon-${addon.id}`}
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => handleDelete(addon.id)}
              className="text-red-400 hover:text-red-300 p-2"
              data-testid={`delete-addon-${addon.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Loading add-ons...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-200">
        <div>
          <h3 className="text-lg text-[#181B1F] font-medium flex items-center gap-2">
            <Package className="w-5 h-5 text-primary" />
            Booking Options / Extras
          </h3>
          <p className="text-gray-500 text-sm">{vehicleName}</p>
        </div>
        {!newAddon && (
          <Button
            type="button"
            size="sm"
            onClick={() => setNewAddon({})}
            className="btn-primary"
            data-testid="add-new-addon"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Option
          </Button>
        )}
      </div>

      {/* New Addon Form */}
      {newAddon && (
        <div>
          <Label className="text-gray-500 text-xs mb-2 block">New Add-on</Label>
          <AddonRow addon={newAddon} isNew={true} />
        </div>
      )}

      {/* Existing Addons */}
      <div className="space-y-2">
        {addons.length === 0 && !newAddon ? (
          <div className="text-center py-8 bg-gray-50 border border-gray-200 rounded-sm">
            <Package className="w-8 h-8 text-white/20 mx-auto mb-2" />
            <p className="text-gray-400">No add-ons configured for this vehicle</p>
            <p className="text-gray-400 text-sm mt-1">Click "Add Option" to create booking extras</p>
          </div>
        ) : (
          addons.map(addon => (
            <AddonRow 
              key={addon.id} 
              addon={addon} 
              isEditing={editingId === addon.id}
            />
          ))
        )}
      </div>

      {/* Info */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-3 mt-4">
        <p className="text-blue-400 text-sm">
          <strong>Tip:</strong> Add-ons configured here will appear during checkout for this specific vehicle.
          Use "Per day" for items like GPS or child seats, and "One-time" for services like delivery.
        </p>
      </div>
    </div>
  );
};

export default VehicleAddonsManager;
